import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { getQueryConfig } from './utils/getQueryConfig'

import type { QueryContext, QueryParams } from '@ngb-frontend/shared/types'
import type { PropsWithChildren } from 'react'

const QueryConfigContext = createContext<QueryContext | undefined>(undefined)

export const QueryConfigProvider: React.FC<
  PropsWithChildren<{
    initialQuery?: QueryParams | null
    onQueryChange?: (query: QueryParams | null) => void
  }>
> = ({ children, initialQuery, onQueryChange }) => {
  const [query, setQuery] = useState<QueryParams | null>(initialQuery || null)
  const updateQuery = useCallback(
    (nextQuery: QueryParams | null) => {
      onQueryChange?.(nextQuery)
      setQuery(nextQuery)
    },
    [onQueryChange],
  )

  const context = useMemo(() => {
    const queryConfig = getQueryConfig(query)
    if (!queryConfig) return

    return { ...queryConfig, updateQuery }
  }, [query, updateQuery])

  return (
    <QueryConfigContext.Provider value={context}>
      {children}
    </QueryConfigContext.Provider>
  )
}

export const useQuery = () => {
  const queryConfig = useContext(QueryConfigContext)
  if (!queryConfig) return

  return queryConfig
}
